/**
 * Place entity index page with state dropdown + search
 *
 * Uses GraphQL to fetch places with state information.
 * States are loaded dynamically from GraphQL.
 */

import { canonicalPath } from '@electinfo/common';

const PAGE_SIZE = 50;

/** Fixed document (persisted-query list, sites#277); limit 50 = page size. */
const PLACES_QUERY = `
query Places($state: String, $search: String, $offset: Int!) {
  places(
    where: { state: { eq: $state }, name: { contains: $search } }
    sort: [{ name: ASC }]
    limit: 50
    offset: $offset
  ) {
    id
    name
    state
    state_fips
    place_fips
    locatedInStates {
      id
      name
      state
    }
  }
  placesConnection(where: { state: { eq: $state }, name: { contains: $search } }) {
    totalCount
  }
}
`;

interface GraphQLRequest {
  query: string;
  variables: Record<string, unknown>;
}

interface StateInfo {
  name: string | null;
  state: string;
}

interface PlaceResult {
  id: string;
  name: string;
  state?: string;
  state_fips?: string;
  place_fips?: string;
  locatedInStates?: Array<{
    id?: string;
    name?: string;
    state?: string;
  }>;
}

interface GraphQLResponse {
  data?: {
    places: PlaceResult[];
    placesConnection?: {
      totalCount: number;
    };
  };
  errors?: Array<{ message: string }>;
}

class PlaceIndex {
  private currentState = '';
  private currentQuery = '';
  private currentPage = 1;
  private totalResults = 0;
  private states: StateInfo[] = [];

  constructor() {
    this.init();
  }

  private init(): void {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  private async setup(): Promise<void> {
    this.injectStyles();
    await this.loadStates();
    this.insertUI();
    this.loadFromURL();
  }

  private async loadStates(): Promise<void> {
    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: '{ states(sort: [{ name: ASC }]) { name state } }'
        })
      });

      if (res.ok) {
        const data = await res.json();
        this.states = (data.data?.states || []).filter((s: StateInfo) => s.name);
      }
    } catch (err) {
      console.error('Failed to load states:', err);
    }
  }

  private getStateName(code: string): string {
    const state = this.states.find(s => s.state === code);
    return state?.name || code;
  }

  private injectStyles(): void {
    const style = document.createElement('style');
    style.textContent = `
      .place-container {
        background: white;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
        border: 1px solid var(--border, #e2e8f0);
      }
      .place-filters {
        display: flex;
        gap: 10px;
        align-items: center;
        flex-wrap: wrap;
        margin-bottom: 15px;
      }
      .place-filters label {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .place-filters select {
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
        min-width: 200px;
        cursor: pointer;
      }
      .place-filters select:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .place-search-form {
        display: flex;
        gap: 10px;
        flex: 1;
        min-width: 250px;
      }
      .place-search-form input {
        flex: 1;
        padding: 12px 16px;
        font-size: 1rem;
        border: 2px solid var(--border, #e2e8f0);
        border-radius: 6px;
        font-family: inherit;
      }
      .place-search-form input:focus {
        outline: none;
        border-color: var(--secondary, #2c5282);
      }
      .place-search-form button {
        padding: 12px 24px;
        font-size: 1rem;
        background: var(--secondary, #2c5282);
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-family: inherit;
      }
      .place-search-form button:hover {
        background: var(--primary, #1a365d);
      }
      .place-results {
        margin-top: 15px;
      }
      .place-results-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border, #e2e8f0);
      }
      .place-results-header h3 {
        margin: 0;
        color: var(--primary, #1a365d);
      }
      .place-clear {
        background: none;
        border: 1px solid var(--border, #e2e8f0);
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
      }
      .place-clear:hover {
        background: var(--bg, #f7fafc);
      }
      .place-result {
        display: block;
        padding: 12px 16px;
        border: 1px solid var(--border, #e2e8f0);
        border-radius: 6px;
        margin-bottom: 8px;
        text-decoration: none;
        color: inherit;
      }
      .place-result:hover {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .place-result-name {
        font-weight: 600;
        color: var(--primary, #1a365d);
      }
      .place-result-meta {
        font-size: 0.9em;
        color: #718096;
        margin-top: 2px;
      }
      .place-message {
        padding: 20px;
        text-align: center;
        color: #718096;
      }
      .place-error {
        color: #c53030;
        background: #fff5f5;
        border-radius: 6px;
      }
      .place-loading {
        text-align: center;
        padding: 40px;
        color: #718096;
      }
      .place-loading::after {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        border: 2px solid #e2e8f0;
        border-top-color: var(--secondary, #2c5282);
        border-radius: 50%;
        animation: place-spin 0.8s linear infinite;
        margin-left: 10px;
        vertical-align: middle;
      }
      @keyframes place-spin {
        to { transform: rotate(360deg); }
      }
      .place-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid var(--border, #e2e8f0);
        flex-wrap: wrap;
      }
      .place-pagination-info {
        width: 100%;
        text-align: center;
        color: #718096;
        font-size: 0.9em;
        margin-bottom: 10px;
      }
      .place-pagination button {
        padding: 8px 12px;
        border: 1px solid var(--border, #e2e8f0);
        background: white;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.9em;
        min-width: 40px;
      }
      .place-pagination button:hover:not(:disabled) {
        background: var(--bg, #f7fafc);
        border-color: var(--secondary, #2c5282);
      }
      .place-pagination button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .place-pagination button.active {
        background: var(--secondary, #2c5282);
        color: white;
        border-color: var(--secondary, #2c5282);
      }
      .place-index-hidden {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  private insertUI(): void {
    const container = document.createElement('div');
    container.className = 'place-container';

    const stateOptions = this.states
      .map(s => `<option value="${s.state}">${s.name}</option>`)
      .join('');

    container.innerHTML = `
      <div class="place-filters">
        <label for="place-state-select">State:</label>
        <select id="place-state-select">
          <option value="">All States</option>
          ${stateOptions}
        </select>
        <form class="place-search-form" id="place-search-form">
          <input type="text" id="place-search-input" placeholder="Search places..." autocomplete="off">
          <button type="submit">Search</button>
        </form>
      </div>
      <div class="place-results" id="place-results">
        <div class="place-results-header">
          <h3 id="place-results-title">All Places</h3>
          <button type="button" class="place-clear" id="place-clear" style="display: none;">Clear Filters</button>
        </div>
        <div id="place-results-list"></div>
        <div id="place-pagination"></div>
      </div>
    `;

    const listView = document.getElementById('list-view');
    if (listView) {
      listView.appendChild(container);
    } else {
      const header = document.querySelector('header');
      const insertPoint = header?.nextElementSibling;
      if (insertPoint?.parentNode) {
        insertPoint.parentNode.insertBefore(container, insertPoint);
      }
    }

    // Hide any existing static content
    document.querySelectorAll('.alphabetical-index, section > ul, main > section').forEach(el => {
      el.classList.add('place-index-hidden');
    });

    // Event listeners
    document.getElementById('place-state-select')?.addEventListener('change', (e) => {
      const select = e.target as HTMLSelectElement;
      this.loadPlaces({ state: select.value, query: this.currentQuery, page: 1 });
    });

    document.getElementById('place-search-form')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const input = document.getElementById('place-search-input') as HTMLInputElement;
      this.loadPlaces({ state: this.currentState, query: input?.value.trim() || '', page: 1 });
    });

    document.getElementById('place-clear')?.addEventListener('click', () => {
      const stateSelect = document.getElementById('place-state-select') as HTMLSelectElement;
      const searchInput = document.getElementById('place-search-input') as HTMLInputElement;
      if (stateSelect) stateSelect.value = '';
      if (searchInput) searchInput.value = '';
      this.loadPlaces({ state: '', query: '', page: 1 });
    });

    window.addEventListener('popstate', () => this.loadFromURL());
  }

  private loadFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const state = urlParams.get('state') || '';
    const query = urlParams.get('q') || '';
    const page = parseInt(urlParams.get('page') || '1', 10) || 1;

    const stateSelect = document.getElementById('place-state-select') as HTMLSelectElement;
    const searchInput = document.getElementById('place-search-input') as HTMLInputElement;
    if (stateSelect) stateSelect.value = state;
    if (searchInput) searchInput.value = query;

    this.loadPlaces({ state, query, page, updateHistory: false });
  }

  private async loadPlaces(options: { state?: string; query?: string; page?: number; updateHistory?: boolean } = {}): Promise<void> {
    const state = options.state ?? this.currentState;
    const query = options.query ?? this.currentQuery;
    const page = options.page || 1;
    const updateHistory = options.updateHistory !== false;

    const resultsList = document.getElementById('place-results-list');
    const paginationContainer = document.getElementById('place-pagination');
    const resultsTitle = document.getElementById('place-results-title');
    const clearButton = document.getElementById('place-clear');

    this.currentState = state;
    this.currentQuery = query;
    this.currentPage = page;

    if (resultsList) resultsList.innerHTML = '<div class="place-loading">Loading</div>';
    if (paginationContainer) paginationContainer.innerHTML = '';

    // Update title
    let title = 'All Places';
    if (state && query) {
      title = `"${query}" in ${this.getStateName(state)}`;
    } else if (state) {
      title = `${this.getStateName(state)} Places`;
    } else if (query) {
      title = `Search: "${query}"`;
    }
    if (resultsTitle) resultsTitle.textContent = title;
    if (clearButton) clearButton.style.display = (state || query) ? 'block' : 'none';

    if (updateHistory) {
      this.updateURL(state, query, page);
    }

    const graphqlQuery = this.buildGraphQLQuery(state, query, page);

    try {
      const res = await fetch('/api/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(graphqlQuery)
      });

      if (!res.ok) {
        if (resultsList) resultsList.innerHTML = `<p class="place-message place-error">Failed to load: ${res.status}</p>`;
        return;
      }

      const response: GraphQLResponse = await res.json();

      if (response.errors) {
        if (resultsList) resultsList.innerHTML = `<p class="place-message place-error">Error: ${response.errors[0]?.message || 'GraphQL error'}</p>`;
        return;
      }

      const places = response.data?.places || [];
      this.totalResults = response.data?.placesConnection?.totalCount || places.length;

      this.displayResults(places);
      this.displayPagination(page, this.totalResults);

    } catch (err) {
      if (resultsList) resultsList.innerHTML = `<p class="place-message place-error">Error: ${(err as Error).message}</p>`;
    }
  }

  private buildGraphQLQuery(state: string, searchQuery: string, page: number): GraphQLRequest {
    return {
      query: PLACES_QUERY,
      variables: {
        offset: (page - 1) * PAGE_SIZE,
        ...(state && { state }),
        ...(searchQuery && { search: searchQuery })
      }
    };
  }

  private displayResults(places: PlaceResult[]): void {
    const resultsList = document.getElementById('place-results-list');
    if (!resultsList) return;

    if (!places.length) {
      resultsList.innerHTML = '<p class="place-message">No places found.</p>';
      return;
    }

    const html = places.map(place => {
      const name = place.name || '';
      const stateCode = place.state || '';
      const displayStateName = place.locatedInStates?.[0]?.name || this.getStateName(stateCode);
      const url = `/${canonicalPath('Place', place.id)}/`;

      return `
        <a href="${url}" class="place-result">
          <div class="place-result-name">${this.escapeHtml(name)}</div>
          <div class="place-result-meta">${this.escapeHtml(displayStateName)}</div>
        </a>
      `;
    }).join('');

    resultsList.innerHTML = html;
  }

  private displayPagination(page: number, total: number): void {
    const paginationContainer = document.getElementById('place-pagination');
    if (!paginationContainer) return;

    const totalPages = Math.ceil(total / PAGE_SIZE);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = total > 0
        ? `<div class="place-pagination-info">Showing all ${total.toLocaleString()} places</div>`
        : '';
      return;
    }

    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);

    let html = `<div class="place-pagination-info">Showing ${start.toLocaleString()}-${end.toLocaleString()} of ${total.toLocaleString()} places</div>`;
    html += '<div class="place-pagination">';
    html += `<button type="button" data-page="1" ${page === 1 ? 'disabled' : ''}>First</button>`;
    html += `<button type="button" data-page="${page - 1}" ${page === 1 ? 'disabled' : ''}>Prev</button>`;

    const pageNumbers = this.getPageNumbers(page, totalPages);
    let lastNum = 0;

    for (const num of pageNumbers) {
      if (num - lastNum > 1) {
        html += '<span style="padding: 8px 4px; color: #718096;">...</span>';
      }
      html += `<button type="button" data-page="${num}" class="${num === page ? 'active' : ''}">${num}</button>`;
      lastNum = num;
    }

    html += `<button type="button" data-page="${page + 1}" ${page === totalPages ? 'disabled' : ''}>Next</button>`;
    html += `<button type="button" data-page="${totalPages}" ${page === totalPages ? 'disabled' : ''}>Last</button>`;
    html += '</div>';

    paginationContainer.innerHTML = html;

    paginationContainer.querySelectorAll('button[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetPage = parseInt((btn as HTMLButtonElement).dataset.page || '1', 10);
        if (targetPage >= 1 && targetPage <= totalPages && targetPage !== page) {
          this.loadPlaces({ page: targetPage });
          document.getElementById('place-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  private getPageNumbers(current: number, total: number): number[] {
    const pages: number[] = [];
    const delta = 2;

    pages.push(1);
    for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
      pages.push(i);
    }
    if (total > 1) pages.push(total);

    return [...new Set(pages)].sort((a, b) => a - b);
  }

  private updateURL(state: string, query: string, page: number): void {
    const url = new URL(window.location.href);

    if (state) {
      url.searchParams.set('state', state);
    } else {
      url.searchParams.delete('state');
    }

    if (query) {
      url.searchParams.set('q', query);
    } else {
      url.searchParams.delete('q');
    }

    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.pushState({ state, query, page }, '', url.toString());
  }

  private escapeHtml(str: string | undefined): string {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

// Auto-initialize
new PlaceIndex();
